import { generatePath, Redirect, Route, Switch, useLocation, useParams } from 'quartz-routing/react-router';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import Homepage from './routes/Homepage';
import TrackPageRouter from './containers/TrackPage/TrackPageRouter';
import Catalog from './routes/Catalog';
import { PROFILE_URL_FRAGMENT } from './constants/Navigation';
import { selectACRoutes } from 'academy-common-lib/routes/academyRoutes';
import TrackAwardModalContainer from './containers/TracksPage/TrackAwardModalContainer';
import { EngagementTracking } from './EngagementTracking';
import NewUserLanding from './components/Landing/NewUserLanding';
import AchievementShareControls from './components/AchievementShareControls';
import AchievementPreviewModalByCredential from './components/Modals/AchievementPreviewModalByCredential';
import MyPlaylists from './routes/MyPlaylists';
import Playlist from './routes/Playlist';
import enviro from 'enviro';
import { useShowDevtools } from './features/devtools/devtoolsQueries';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { ShortContentRoute } from './routes/ShortContent';
import { MyLearningTabs } from './features/myLearning';
import { getOverviewModalParams } from 'academy-common-lib/utils/url';
import { SoftwareDiscoveryIframe } from './features/toolRecommendations';
import Wizard from './routes/Wizard';
import HubspotPlaylistsAdmin from './features/hubspotPlaylists/components/hubspotPlaylistsAdmin/HubspotPlaylistsAdminMainPage';
import MyTeamOverview from './features/myTeam/routes/MyTeamOverview';
import MyTeamCertificationsIndex from './features/myTeam/routes/MyTeamCertificationsIndex';
import MyTeamMembers from './features/myTeam/routes/MyTeamMembers';
import { MyProfile, UserProfile, EditProfile } from './routes/Profile';
import MyTeamCertificationProfile, { RedirectToCertificationProfileDefaultTab } from './features/myTeam/routes/MyTeamCertificationProfile';
import MyTeamCertificationsByTeam, { RedirectToCertificationsByTeamDefaultTab } from './features/myTeam/routes/MyTeamCertificationsByTeam';
import MyTeamPlaylists, { RedirectToPlaylistsDefaultTab } from './features/myTeam/routes/MyTeamPlaylists';
import ReactRouterScrollToTop from 'academy-common-lib/components/routing/ReactRouterScrollToTop';
import WatchLater from './routes/WatchLater';
import { RedirectToMyTeamOverview } from './features/myTeam/routes/MyTeamOverview';
import { PracticeMode } from './features/practiceMode/PracticeMode';
import AcademyLabs from './routes/AcademyLabs';
import { AcademyLabsSession } from './features/academyLabs/components/AcademyLabsSession';
import AcademyOnboarding from './features/academyOnboarding/routes/AcademyOnboarding';
import AcademyAssistant from './routes/AcademyAssistant';
import { NavPageRoute } from './features/navigation/components/NavPageRoute';
import { RedirectToMyLearning } from './features/myLearning/myLearningRouting';
import LessonPageRouter from './routes/Lesson';
import ReactRouterSmoothScrollToTop from 'academy-common-lib/components/routing/ReactRouterSmoothScrollToTop';
import NotFound from './routes/NotFound';
import { HasGate } from './features/accounts';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AC_ROUTES = selectACRoutes();
const RedirectToTracksWithQueryParams = () => {
  const location = useLocation();
  const {
    pathname,
    search
  } = location;
  if (/cat=|tools=|awardType=|level=|duration=|contentType=|language=/i.test(search)) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: Object.assign({}, location, {
        pathname: AC_ROUTES.RESULTS
      })
    });
  }
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: pathname.replace('/tracks', '')
    })
  });
};
const RedirectToAward = () => {
  const location = useLocation();
  const {
    pathname
  } = location;
  const {
    trackId,
    awardName
  } = useParams();
  const legacyPath = generatePath(AC_ROUTES.LEGACY_AWARD, {
    trackId,
    awardName
  });
  const newPath = generatePath(AC_ROUTES.AWARD, {
    trackId
  });
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: pathname.replace(legacyPath, newPath)
    })
  });
};
const RedirectToWatchLater = () => {
  const location = useLocation();
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: AC_ROUTES.WATCH_LATER
    })
  });
};
const RedirectToNewOverview = () => {
  const location = useLocation();
  const {
    prefix,
    type,
    id,
    source,
    language
  } = useParams();
  const search = new URLSearchParams(location.search);
  const newParams = getOverviewModalParams({
    id: Number(id),
    type: type === 'course-overview' ? 'TRACK' : type === 'lesson-overview' ? 'LESSON' : 'SHORT_FORM_CONTENT',
    language,
    source
  });
  Object.entries(newParams).forEach(([key, value]) => {
    search.set(key, value);
  });
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: prefix,
      search: `?${search}`
    })
  });
};
const PartnerPage = Loadable({
  loader: () => import( /* webpackChunkName: "bundles/partners" */
  './routes/Partners').then(mod => mod.default)
});
const QaOnlyRoutes = Loadable({
  loader: () => import( /* webpackChunkName: "bundles/qaOnly" */'./components/QaOnly/QaOnlyRoutes').then(mod => mod.default)
});
const Devtools = Loadable({
  loader: () => import( /* webpackChunkName: "bundles/devtools" */'./features/devtools').then(mod => mod.Devtools),
  LoadingComponent: () => null
});
const RemoveTrailingSlash = () => {
  const location = useLocation();
  const {
    pathname
  } = location;
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: pathname.replace(/\/+$/, '')
    })
  });
};
export const MyPlaylistsRoutePaths = AC_ROUTES.MY_PLAYLISTS;
export const WizardRoutePaths = AC_ROUTES.WIZARD;
export const Routes = () => {
  const showDevtools = useShowDevtools();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), showDevtools && /*#__PURE__*/_jsx(Devtools, {}), /*#__PURE__*/_jsx(NewUserLanding, {}), /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        component: RemoveTrailingSlash,
        path: "/*(/+)"
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: SoftwareDiscoveryIframe,
        pageTitleI18nKey: "learningCenter.pageTitles.softwareDiscovery",
        path: AC_ROUTES.SOFTWARE_DISCOVERY,
        shouldRenderFullScreen: true
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: PracticeMode,
        pageTitleI18nKey: "learningCenter.pageTitles.practiceMode",
        path: AC_ROUTES.PRACTICE_MODE,
        shouldRenderFullScreen: true
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: AcademyLabs,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.academyLabs",
        path: AC_ROUTES.AC_LABS
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: AcademyLabsSession,
        pageTitleI18nKey: "learningCenter.pageTitles.academyLabsSession",
        path: AC_ROUTES.AC_LABS_SESSION,
        shouldRenderFullScreen: true
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: AcademyOnboarding,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.academyOnboarding",
        path: AC_ROUTES.AC_ONBOARDING,
        shouldRenderCenteredContent: false
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: AcademyAssistant,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.academyAssistant",
        path: AC_ROUTES.AC_ASSISTANT,
        shouldRenderCenteredContent: false
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: Wizard,
        pageTitleI18nKey: "learningCenter.pageTitles.findYourPath",
        path: AC_ROUTES.WIZARD
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: TrackPageRouter,
        pageTitleI18nKey: "learningCenter.pageTitles.course",
        path: AC_ROUTES.TRACK_PAGE,
        shouldRenderCenteredContent: false,
        shouldRenderCompactSearchBar: true,
        shouldRenderTabs: false
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: LessonPageRouter,
        pageTitleI18nKey: "learningCenter.pageTitles.lesson",
        path: AC_ROUTES.LESSON_PAGE,
        shouldRenderCenteredContent: false,
        shouldRenderCompactSearchBar: true,
        shouldRenderTabs: false
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: AchievementShareControls,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.shareAchievement",
        path: AC_ROUTES.SHARE_ACHIEVEMENT,
        shouldRenderFullScreen: true
      }), /*#__PURE__*/_jsx(Route, {
        path: `/award-share/:trackId(\\d+)`,
        render: () => /*#__PURE__*/_jsx(Redirect, {
          to: PROFILE_URL_FRAGMENT
        })
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToNewOverview,
        path: AC_ROUTES.LEGACY_OVERVIEW
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: Homepage,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.home",
        path: AC_ROUTES.HOME,
        shouldRenderCenteredContent: false,
        shouldRenderSearch: false
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: Catalog,
        pageTitleI18nKey: "learningCenter.pageTitles.browseCourses",
        path: AC_ROUTES.RESULTS
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: PartnerPage,
        pageTitleI18nKey: "learningCenter.pageTitles.partnerCourses",
        path: AC_ROUTES.PARTNERS
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToMyTeamOverview,
        exact: true,
        path: AC_ROUTES.LEGACY_MY_TEAM
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamOverview,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeam",
        path: AC_ROUTES.MY_TEAM
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamCertificationsIndex,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamCertifications",
        path: AC_ROUTES.MY_TEAM_CERTIFICATIONS
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamMembers,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamMembers",
        path: AC_ROUTES.MY_TEAM_MEMBERS
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamCertificationProfile,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.certification",
        path: AC_ROUTES.MY_TEAM_CERTIFICATION_PROFILE
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: RedirectToCertificationProfileDefaultTab,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.certification",
        path: AC_ROUTES.MY_TEAM_CERTIFICATION_PROFILE_REDIRECT
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamCertificationsByTeam,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamCertificationsByTeam",
        path: AC_ROUTES.MY_TEAM_CERTIFICATIONS_BY_TEAM
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: RedirectToCertificationsByTeamDefaultTab,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamCertificationsByTeam",
        path: AC_ROUTES.MY_TEAM_CERTIFICATIONS_BY_TEAM_REDIRECT
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamPlaylists,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamPlaylists",
        path: AC_ROUTES.MY_TEAM_PLAYLISTS
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: RedirectToPlaylistsDefaultTab,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamPlaylists",
        path: AC_ROUTES.MY_TEAM_PLAYLISTS_REDIRECT
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: EditProfile,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.editProfile",
        path: AC_ROUTES.EDIT_PROFILE
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: UserProfile,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.userProfile",
        path: AC_ROUTES.USER_PROFILE
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyProfile,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myProfile",
        path: AC_ROUTES.PROFILE
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyLearningTabs,
        pageTitleI18nKey: "learningCenter.pageTitles.myLearning",
        path: AC_ROUTES.MY_LEARNING
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToMyLearning,
        exact: false,
        path: AC_ROUTES.LEGACY_MY_LEARNING
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.hubspotPlaylistsAdmin",
        path: AC_ROUTES.HUBSPOT_PLAYLISTS_ADMIN,
        children: /*#__PURE__*/_jsx(HubspotPlaylistsAdmin, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: AC_ROUTES.SAVED_ITEMS_PLAYLISTS,
        children: /*#__PURE__*/_jsxs(Switch, {
          children: [/*#__PURE__*/_jsx(NavPageRoute, {
            exact: true,
            pageTitleI18nKey: "learningCenter.pageTitles.myPlaylists",
            path: AC_ROUTES.MY_PLAYLISTS,
            children: /*#__PURE__*/_jsx(MyPlaylists, {})
          }), /*#__PURE__*/_jsx(NavPageRoute, {
            component: Playlist,
            exact: true,
            pageTitleI18nKey: "learningCenter.pageTitles.playlist",
            path: AC_ROUTES.PLAYLIST,
            shouldRenderCenteredContent: false
          })]
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: AC_ROUTES.SAVED_ITEMS,
        children: /*#__PURE__*/_jsx(Switch, {
          children: /*#__PURE__*/_jsx(NavPageRoute, {
            component: RedirectToWatchLater,
            pageTitleI18nKey: "learningCenter.pageTitles.watchLater",
            path: AC_ROUTES.LEGACY_WATCH_LATER
          })
        })
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: WatchLater,
        pageTitleI18nKey: "learningCenter.pageTitles.watchLater",
        path: AC_ROUTES.WATCH_LATER
      }), enviro.isQa() && /*#__PURE__*/_jsx(Route, {
        component: QaOnlyRoutes,
        path: AC_ROUTES.QA_ONLY
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: AC_ROUTES.LIVE_EVENT_PREVIEW,
        render: ({
          location
        }) => {
          const params = new URLSearchParams(location.search);
          params.set('overviewType', 'LIVE_EVENT');
          params.set('overviewEntityId', '1001');
          return /*#__PURE__*/_jsx(HasGate, {
            gate: "AcademyAchievements::Onboarding::LiveEvents",
            children: /*#__PURE__*/_jsx(Redirect, {
              to: {
                pathname: '/',
                search: `?${params.toString()}`
              }
            })
          });
        }
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToTracksWithQueryParams,
        path: AC_ROUTES.TRACKS
      }), /*#__PURE__*/_jsx(Route, {
        path: AC_ROUTES.SHORT_VIDEO,
        children: /*#__PURE__*/_jsx(ShortContentRoute, {})
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToAward,
        exact: true,
        path: AC_ROUTES.LEGACY_AWARD
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: TrackAwardModalContainer,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.award",
        path: AC_ROUTES.AWARD
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: NotFound,
        pageTitleI18nKey: "learningCenter.pageTitles.notFound",
        path: "*"
      })]
    }), /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        component: RedirectToAward,
        path: AC_ROUTES.LEGACY_AWARD_OVERLAY
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: TrackAwardModalContainer,
        pageTitleI18nKey: "learningCenter.pageTitles.award",
        path: AC_ROUTES.AWARD_OVERLAY
      })]
    }), /*#__PURE__*/_jsx(NavPageRoute, {
      component: AchievementPreviewModalByCredential,
      pageTitleI18nKey: "learningCenter.pageTitles.shareAchievementPreview",
      path: AC_ROUTES.SHARE_ACHIEVEMENT_PREVIEW
    }), /*#__PURE__*/_jsx(Route, {
      component: EngagementTracking,
      path: AC_ROUTES.TRACK_BY_ID
    }), /*#__PURE__*/_jsx(Route, {
      component: EngagementTracking,
      path: AC_ROUTES.LESSON_BY_ID
    }), /*#__PURE__*/_jsx(Route, {
      component: ReactRouterSmoothScrollToTop,
      path: AC_ROUTES.TRACKS
    }), /*#__PURE__*/_jsx(Route, {
      component: ReactRouterSmoothScrollToTop,
      path: AC_ROUTES.LESSONS
    }), /*#__PURE__*/_jsx(Route, {
      component: ReactRouterSmoothScrollToTop,
      path: AC_ROUTES.SHORT_VIDEO
    }), /*#__PURE__*/_jsx(Route, {
      component: ReactRouterScrollToTop,
      path: AC_ROUTES.MY_TEAM
    })]
  });
};
export default Routes;